import { Pencil } from 'lucide-react';

import React, { useMemo } from 'react';

import { Avatar as AvatarComp, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import { cn } from '@/lib/utils';

import { getGradientForName } from '@/utils/avatar-utils';

import { useAppStore } from '../contexts/app-wrapper/providers/app-provider';
import { CopyItem } from './copy-item';

export default function Avatar({
	name,
	className,
	fallbackClassName,
	small = false,
	imageUrl,
	squared,
}: {
	small?: boolean;
	name: string;
	className?: string;
	fallbackClassName?: string;
	imageUrl?: string;
	squared?: boolean;
}) {
	const workspaceImageUrlsString = useAppStore((state) =>
		state?.workspaces?.map((item) => item?.logo)?.join(' ')
	);
	const workspaceNameString = useAppStore((state) =>
		state?.workspaces?.map((item) => item?.name)?.join(',')
	);

	const shouldBeSquared = useMemo(() => {
		if (squared !== undefined) return squared;
		const isWorkspaceImage = imageUrl ? workspaceImageUrlsString?.includes(imageUrl) : false;
		const isWorkspaceName = name ? workspaceNameString?.split(',')?.includes(name) : false;

		if (imageUrl) return isWorkspaceImage;
		return isWorkspaceName;
	}, [workspaceImageUrlsString, squared, imageUrl, name]);

	return (
		<AvatarComp
			className={cn(
				'h-9 w-9 flex-shrink-0',
				small && 'h-6 w-6',
				shouldBeSquared && 'rounded',
				className
			)}
		>
			{imageUrl && <AvatarImage src={imageUrl} />}

			<AvatarFallback
				style={{ background: getGradientForName(name) }}
				className={cn(fallbackClassName, small && 'text-xs', shouldBeSquared && 'rounded')}
			>
				{name?.slice(0, 1)?.toUpperCase()}
			</AvatarFallback>
		</AvatarComp>
	);
}

export function AvatarWithDetails({
	name,
	avatarClassName,
	avatarFallbackClassName,
	email,
	small = false,
	medium = false,
	className,
	onClick,
	selectable = false,
	copyEmail = false,
	imageUrl,
	textClassName,
	squared,
	descriptionContent,
	onEditImage,
	onEditName,
}: {
	className?: string;
	small?: boolean;
	medium?: boolean;
	name: string;
	avatarClassName?: string;
	avatarFallbackClassName?: string;
	email: string;
	onClick?: () => void;
	selectable?: boolean;
	copyEmail?: boolean;
	imageUrl?: string;
	textClassName?: string;
	squared?: boolean;
	descriptionContent?: React.ReactNode;
	/** When provided, shows a pencil overlay on the avatar that triggers image editing on click. */
	onEditImage?: () => void;
	/** When provided, shows a pencil icon next to the name on hover. */
	onEditName?: () => void;
}) {
	const emailJsx = (
		<p
			data-email-copy
			className={cn(
				'w-fit max-w-full truncate text-[13px] text-secondary',
				small && 'text-[10px]',
				selectable && 'select-text'
			)}
		>
			{email}
		</p>
	);

	const avatarEl = (
		<Avatar
			name={name || email}
			className={avatarClassName}
			fallbackClassName={avatarFallbackClassName}
			small={small}
			imageUrl={imageUrl}
			squared={squared}
		/>
	);

	return (
		<div
			className={cn(
				'flex w-full items-center gap-3 text-start',
				className,
				small && 'gap-2',
				onClick && 'cursor-pointer',
				!onClick && !copyEmail && 'cursor-default'
			)}
			onClick={onClick}
			role={onClick ? 'button' : undefined}
			tabIndex={onClick ? 0 : undefined}
			onKeyDown={
				onClick
					? (e) => {
							if (e.key === 'Enter' || e.key === ' ') onClick();
						}
					: undefined
			}
		>
			{onEditImage ? (
				<button
					type="button"
					className={cn(
						'group/avatar relative flex-shrink-0 cursor-pointer border-none outline-none'
					)}
					onClick={(e) => {
						e.stopPropagation();
						onEditImage();
					}}
				>
					{avatarEl}
					<div
						className={cn(
							'absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover/avatar:opacity-100',
							squared === false ? 'rounded-full' : 'rounded'
						)}
					>
						<Pencil className="size-3.5 text-white" />
					</div>
				</button>
			) : (
				avatarEl
			)}

			<div className={cn('flex-1 overflow-hidden', medium && 'font-normal', textClassName)}>
				<div className="flex items-center gap-1">
					<h2
						className={cn(
							'mb-0 cursor-text select-text truncate text-[15px] font-semibold text-primary',
							small && 'mb-0 text-[12px]',
							medium && 'mb-0 text-[14px] font-medium',
							selectable && 'select-text'
						)}
					>
						{name}
					</h2>
					{onEditName && (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onEditName();
							}}
							className="flex-shrink-0 rounded p-0.5 opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100"
						>
							<Pencil className="size-3 text-muted-foreground" />
						</button>
					)}
				</div>

				{descriptionContent ? (
					descriptionContent
				) : copyEmail ? (
					<CopyItem textToCopy={email}>{emailJsx}</CopyItem>
				) : (
					emailJsx
				)}
			</div>
		</div>
	);
}
